// Acquisition attribution + deposit-gated referral binding (ADR-0042).
//
// Replaces the launch-grant claim path (ADR-0037, retired): there is no
// unconditional signup credit — the first-topup match is issued by the
// backend when a top-up settles, and referral binding is the only
// acquisition action left for the client. The marketing surface still
// marks which CTA opened the signup (channel + opaque source slug — never
// a URL or free text) and invite links carry `?ref=<inviter org id>`.
//
// On the org's first authenticated interaction the org-scoped ReferralBind
// component POSTs the pending code through resources/acquisition.bindReferral
// — the backend's "one binding per invitee" partial-unique index is the
// authority, and the referral_rewards aging row is what actually releases
// the ¥20/¥20 pair seven days after the invitee's first top-up settles
// without a refund. The localStorage latch below exists only to stop this
// browser from re-firing the call on every reload — a definitive answer or
// a dropped code closes it. A stale latch means one extra bind call that
// answers existing, never a repeated reward.
//
// Everything below stays deliberately narrow: whitelisted channel, bounded
// slug, a uuid-shaped referral code, and the org-keyed latch. Tracking
// params, cookies, device fingerprinting — not here.

import { refBoundKey, SK } from './storageKeys'

/** Attributed channel whitelist — mirrors the backend acquisition channel
 *  enum (docs/adr/0042, resources/acquisition.ts pins it to the wire).
 *  `unknown` is the catch-all for entries whose channel cannot be named. */
export const ACQUISITION_CHANNELS = ['landing', 'docs', 'console', 'referral', 'partner', 'ops', 'unknown'] as const
export type AcquisitionChannel = (typeof ACQUISITION_CHANNELS)[number]

/** The wire reason union for POST /acquisition/referral-bind — kept local
 *  rather than re-deriving from the OpenAPI schema so a contract change
 *  fails loudly here (resources/acquisition.ts pins it to the wire). */
export type ReferralBindReason = 'bound' | 'existing' | 'self_referral' | 'unknown_inviter'

const SOURCE_SLUG_MAX = 64

/** attributionSlug — the marketing-side guardrail. Free-form ?utm_source=-
 *  style values would otherwise smuggle URLs and raw user text into the
 *  fact row; we only accept short lowercase slugs and drop anything else
 *  instead of sending junk the server must refuse. */
export function attributionSlug(raw: string | null | undefined): string | null {
  const slug = raw
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SOURCE_SLUG_MAX)
  return slug && slug.length > 0 ? slug : null
}

export type AcquisitionEntry = { channel: AcquisitionChannel; source: string | null }

/** readAcquisitionEntry — returns the stored CTA marker, or null when none
 *  has been recorded. A corrupted/out-of-whitelist channel converges to
 *  `unknown` rather than failing: entry markers are facts, never errors. */
export function readAcquisitionEntry(): AcquisitionEntry | null {
  try {
    const channel = window.localStorage.getItem(SK.acquisitionEntry)
    if (!channel) return null
    const safe = (ACQUISITION_CHANNELS as readonly string[]).includes(channel)
      ? (channel as AcquisitionChannel)
      : 'unknown'
    return { channel: safe, source: attributionSlug(window.localStorage.getItem(SK.acquisitionSource)) }
  } catch {
    return null
  }
}

/** markAcquisitionEntry — records where a signup CTA click came from.
 *  Idempotent under repeated clicks; attribution is first-write-wins
 *  upstream but this browser's marker is intentionally overwritten only
 *  by an explicit new CTA mark, not by ensureAcquisitionEntry(). */
export function markAcquisitionEntry(channel: AcquisitionChannel, source?: string | null): void {
  try {
    window.localStorage.setItem(SK.acquisitionEntry, channel)
    const slug = attributionSlug(source)
    if (slug) window.localStorage.setItem(SK.acquisitionSource, slug)
    else window.localStorage.removeItem(SK.acquisitionSource)
  } catch {
    // storage unavailable — the bind still works, attribution just goes unknown
  }
}

/** ensureAcquisitionEntry — fires when the org's first authenticated
 *  interaction arrives without a marker (direct /register visit, bookmark,
 *  in-product nudge). Stamps `unknown` so the referral bind sends a real
 *  row instead of a hole; never overwrites a real channel. */
export function ensureAcquisitionEntry(): void {
  try {
    if (!window.localStorage.getItem(SK.acquisitionEntry)) {
      window.localStorage.setItem(SK.acquisitionEntry, 'unknown')
      window.localStorage.removeItem(SK.acquisitionSource)
    }
  } catch {
    // storage unavailable — same as above
  }
}

// ── referral code capture ────────────────────────────────────────────────
// Invite links land as ?ref=<inviter org id>. The code is a uuid — anything
// else is junk and is dropped instead of being carried to the bind call.
const REF_CODE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** captureReferralCode — stores a pending invite code from the current
 *  URL's `ref` param, or a code handed in directly by the register form.
 *  First-code-wins: a second invite link does not overwrite a pending
 *  one — attribution follows whoever actually brought the user. */
export function captureReferralCode(search?: string, explicit?: string | null): void {
  try {
    const raw = explicit ?? (search !== undefined ? new URLSearchParams(search).get('ref') : null)
    if (!raw || !REF_CODE_RE.test(raw.trim())) return
    if (!window.localStorage.getItem(SK.acquisitionRef)) {
      window.localStorage.setItem(SK.acquisitionRef, raw.trim().toLowerCase())
    }
  } catch {
    // storage unavailable — the invite just doesn't bind on this browser
  }
}

/** pendingReferralCode — the stored invite code awaiting the bind call, or
 *  null. Returning it does NOT consume it: the code clears only when the
 *  endpoint answers definitively (or storage is wiped). */
export function pendingReferralCode(): string | null {
  try {
    return window.localStorage.getItem(SK.acquisitionRef)
  } catch {
    return null
  }
}

/** clearReferralCode — drops the pending invite code after a definitive
 *  bind answer (bound/existing/refused). */
export function clearReferralCode(): void {
  try {
    window.localStorage.removeItem(SK.acquisitionRef)
  } catch {
    // storage unavailable
  }
}

// ── once-per-org bind latch ──────────────────────────────────────────────
// Module-level Set — one bind attempt per org per page load, before any
// network call. Closes permanently via markReferralBound once the endpoint
// answered (or there was no code to send); a reload stops re-attempting.
const inFlightBinds = new Set<string>()

/** beginReferralBind — returns true exactly once per org per page load.
 *  A 5xx/timeout is the only non-definitive outcome: the latch stays open
 *  for the retry on the next load. */
export function beginReferralBind(orgId: string): boolean {
  if (inFlightBinds.has(orgId)) return false
  inFlightBinds.add(orgId)
  return true
}

/** markReferralBound — closes the latch for good: the org has answered
 *  its referral-code question on this browser. */
export function markReferralBound(orgId: string): void {
  inFlightBinds.delete(orgId)
  try {
    window.localStorage.setItem(refBoundKey(orgId), '1')
  } catch {
    // latch already in the module set; persistence is best-effort
  }
}

/** hasBoundReferral — true when the bind was previously resolved here. */
export function hasBoundReferral(orgId: string): boolean {
  if (inFlightBinds.has(orgId)) return true
  try {
    return window.localStorage.getItem(refBoundKey(orgId)) !== null
  } catch {
    return false
  }
}

// ── notices ──────────────────────────────────────────────────────────────
// referralNotice maps the wire answer to a toast; an answer it cannot
// explain stays silent — a refusal is a state, never an error.
export type AcquisitionNotice = { variant: 'success' | 'info'; titleKey: string; descriptionKey?: string }

/** referralNotice — calm notice per answer. `existing` is silent: the user
 *  never asked for a bind, and re-announcing a stale binding is noise. */
export function referralNotice(reason: ReferralBindReason | string): AcquisitionNotice | null {
  switch (reason) {
    case 'bound':
      return { variant: 'success', titleKey: 'acquisition.referral.bound', descriptionKey: 'acquisition.referral.boundNote' }
    case 'self_referral':
      return { variant: 'info', titleKey: 'acquisition.referral.selfReferral' }
    case 'unknown_inviter':
      return { variant: 'info', titleKey: 'acquisition.referral.unknownInviter' }
    default:
      return null
  }
}
